import { useState, useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { EditUserSchema } from "../../FormsValidations/Validations";
import Input from "../../Utility/Input/Input";
import Label from "../../Utility/Label/Label";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import Checkbox from "../../Utility/Checkbox/Checkbox";
import ErrorMsg from "../../Utility/ErrorMsg";
import { logger } from "../../utils/logger";
import { RiEditLine } from "react-icons/ri";
import "./users.css";
import BackArrow from "../../Utility/BackArrow/BackArrow";
import {
  selectTeamsData,
  selectTeamsLoading,
  selectTeamsError,
} from "../../Redux/Slices/teamsSlice";
import {
  selectUsersData,
  selectUsersAllListData,
  selectUserRolesLoading,
  selectUserRolesError,
} from "../../Redux/Slices/usersSlice";
import { selectSystemListStateForType } from "../../Redux/Slices/systemListsSlice";
import { updateUser, fetchUserRoles, fetchUserById } from "../../Redux/Thunks/usersThunk";
import { fetchTeamsData } from "../../Redux/Thunks/teamsThunk";
import { fetchSystemList } from "../../Redux/Thunks/systemListsThunk";
import { findUserInCachedUsersPayloads, getUserFromByIdResponse } from "./usersDataUtils";
import { getTeamsListFromApiData } from "../Teams/teamsDataUtils";
import { getListFromApiResponse, mapListItemToOption, getErrorMessage } from "./userMappers";
import { showSuccess, showError } from "../../utils/toast";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import ExpandableSection from "../../Utility/ExpandableSection/ExpandableSection";
import { ContentLoader } from "../../Utility/Loader";
import { digitsOnly } from "../../utils/digitsOnlyInput";

function nameToFirstLast(name) {
  if (!name || typeof name !== "string") return { firstName: "", lastName: "" };
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

function normalizeRoleToken(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

/** Resolve API job title (id, nested object, or legacy label) to dropdown value matching JOB_TITLE options. */
function resolveJobTitleFormValue(user, jobTitleOptions) {
  if (!user) return "";
  const raw =
    user.jobTitleId ??
    (typeof user.jobTitle === "object" && user.jobTitle != null
      ? (user.jobTitle.id ?? user.jobTitle.name)
      : user.jobTitle) ??
    user.jobTitleName ??
    user.designation ??
    "";
  const rawStr = String(raw ?? "").trim();
  if (!rawStr) return "";
  if (!jobTitleOptions?.length) return rawStr;
  const byValue = jobTitleOptions.find((o) => String(o.value) === rawStr);
  if (byValue) return String(byValue.value);
  const byLabel = jobTitleOptions.find((o) => o.label === rawStr);
  if (byLabel) return String(byLabel.value);
  return rawStr;
}

/** Resolve user role to a dropdown value matching fetched roles (by id, name, or role code). */
function resolveRoleIdFormValue(user, roleList) {
  if (!user) return "";
  const direct =
    user.roleId ??
    (typeof user.role === "object" && user.role != null ? user.role.id : undefined) ??
    "";
  const directStr = String(direct ?? "").trim();
  if (directStr) return directStr;

  const roleToken =
    (typeof user.role === "object" && user.role != null ? user.role.name : user.role) ??
    user.roleCode ??
    "";
  const roleTokenStr = String(roleToken ?? "").trim();
  if (!roleTokenStr || !Array.isArray(roleList) || roleList.length === 0) return "";

  const want = normalizeRoleToken(roleTokenStr);

  const match = roleList.find((r) => {
    const n = String(r?.name ?? "").trim();
    if (n && normalizeRoleToken(n) === want) return true;
    const codes = [r?.code, r?.key, r?.slug, r?.roleCode, r?.value];
    for (const c of codes) {
      if (c != null && normalizeRoleToken(c) === want) return true;
    }
    return false;
  });
  if (match?.id == null) return "";
  return String(match.id).trim();
}

export default function EditUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const teamsData = useSelector(selectTeamsData);
  const teamsLoading = useSelector(selectTeamsLoading);
  const teamsError = useSelector(selectTeamsError);
  const usersListPayload = useSelector(selectUsersData);
  const allListData = useSelector(selectUsersAllListData);
  const userRolesLoading = useSelector(selectUserRolesLoading);
  const userRolesError = useSelector(selectUserRolesError);
  const userRolesData = useSelector((s) => s.users.userRolesData);
  const userRolesLoaded = useSelector((s) => s.users.userRolesLoaded);
  const jobTitleListState = useSelector((s) => selectSystemListStateForType(s, "JOB_TITLE"));
  const prevPath = location.state?.previousPath || "/users";

  const cachedUser = useMemo(
    () => findUserInCachedUsersPayloads(allListData, usersListPayload, id),
    [allListData, usersListPayload, id]
  );

  const [detailUser, setDetailUser] = useState(null);
  const [detailStatus, setDetailStatus] = useState("idle");

  useEffect(() => {
    if (!id) return undefined;
    setDetailStatus("loading");
    setDetailUser(null);
    let cancelled = false;
    dispatch(fetchUserById(id))
      .unwrap()
      .then((raw) => {
        if (cancelled) return;
        const mapped = getUserFromByIdResponse(raw);
        if (!mapped || String(mapped.id ?? "").trim() !== String(id).trim()) {
          setDetailUser(null);
          setDetailStatus("error");
          return;
        }
        setDetailUser(mapped);
        setDetailStatus("success");
      })
      .catch(() => {
        if (cancelled) return;
        setDetailUser(null);
        setDetailStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [id, dispatch]);

  const user = useMemo(() => {
    if (detailUser && cachedUser) return { ...cachedUser, ...detailUser };
    return detailUser ?? cachedUser ?? null;
  }, [detailUser, cachedUser]);

  const teamList = useMemo(() => getTeamsListFromApiData(teamsData) ?? [], [teamsData]);
  const teamOptions = useMemo(
    () =>
      teamList.map((t) => ({
        value: String(t.id ?? ""),
        label: t.teamName ?? t.name ?? String(t.id ?? ""),
      })),
    [teamList]
  );
  const roleList = useMemo(() => getListFromApiResponse(userRolesData ?? []), [userRolesData]);
  const jobTitleList = useMemo(
    () => getListFromApiResponse(jobTitleListState?.list ?? []),
    [jobTitleListState?.list]
  );
  const roleOptions = useMemo(
    () => roleList.map((r) => ({ value: String(r.id), label: r.name })),
    [roleList]
  );
  const jobTitleOptions = useMemo(() => jobTitleList.map(mapListItemToOption), [jobTitleList]);

  const [loading, setLoading] = useState(false);
  const [isContactExpanded, setIsContactExpanded] = useState(true);
  const [isUsernameEditable, setIsUsernameEditable] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(EditUserSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      username: "",
      roleId: "",
      teamId: "",
      jobTitle: "",
      billingRatePerHour: "",
      status: "Active",
      mobileNumber: "",
      whatsappNumber: "",
      receiveWeeklyActivitySummary: false,
    },
  });

  useEffect(() => {
    if (teamsData != null) return;
    dispatch(fetchTeamsData({ getAll: true }));
  }, [dispatch, teamsData]);

  useEffect(() => {
    dispatch(fetchUserRoles({ force: true }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList("JOB_TITLE"));
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      const fromName = nameToFirstLast(user.name);
      const firstName = String(user.firstName ?? "").trim() || fromName.firstName;
      const lastName = String(user.lastName ?? "").trim() || fromName.lastName;
      const emailRaw =
        user.email ??
        user.userEmail ??
        user.primaryEmail ??
        user.contactEmail ??
        user.emailAddress ??
        user.mail ??
        (typeof user.profile === "object" && user.profile != null ? user.profile.email : "") ??
        "";
      const email = String(emailRaw ?? "").trim();
      const usernameFromEmail = email
        ? email
            .split("@")[0]
            ?.toLowerCase()
            .replace(/[^a-z0-9]/g, ".") || ""
        : "";
      const isActive =
        user.is_active === true ||
        user.is_active === 1 ||
        user.status === "Active" ||
        String(user.status ?? "").toLowerCase() === "active";
      reset({
        firstName,
        lastName,
        email,
        username:
          String(user.username ?? user.userName ?? user.login ?? "").trim() || usernameFromEmail,
        roleId: resolveRoleIdFormValue(user, roleList),
        teamId: String(
          user.teamId ?? user.defaultTeamId ?? user.team?.id ?? user.team_id ?? ""
        ).trim(),
        jobTitle: resolveJobTitleFormValue(user, jobTitleOptions),
        billingRatePerHour: (() => {
          const raw =
            user.billingRatePerHour ??
            user.billingRate ??
            user.hourlyRate ??
            user.rate ??
            user.billing_rate_per_hour;
          if (raw == null || raw === "") return "";
          return String(raw);
        })(),
        status: isActive ? "Active" : "Inactive",
        mobileNumber: digitsOnly(
          user.mobile ?? user.mobileNumber ?? user.phone ?? user.phoneNumber ?? ""
        ),
        whatsappNumber: digitsOnly(
          user.whatsappNumber ?? user.whatsAppNumber ?? user.whatsapp ?? ""
        ),
        receiveWeeklyActivitySummary:
          user.receiveWeeklyActivitySummary ?? user.weeklyActivitySummary ?? false,
      });
    }
  }, [user, reset, jobTitleOptions, roleList]);

  const onSubmit = async (formData) => {
    if (!user?.id) return;
    setLoading(true);
    try {
      // PUT /users/:id – payload matches API spec
      const payload = {
        roleId: formData.roleId?.trim() || undefined,
        firstName: formData.firstName?.trim() ?? "",
        lastName: formData.lastName?.trim() ?? "",
        teamId: formData.teamId?.trim() || undefined,
        jobTitle: formData.jobTitle?.trim() ?? "",
        billingRatePerHour: Number(formData.billingRatePerHour) || 0,
        is_active: formData.status === "Active",
        mobileNumber: formData.mobileNumber?.trim() || undefined,
        whatsappNumber: formData.whatsappNumber?.trim() || undefined,
        receiveWeeklyActivitySummary: Boolean(formData.receiveWeeklyActivitySummary),
      };
      await dispatch(updateUser({ id: user.id, userData: payload })).unwrap();
      showSuccess("User updated successfully");
      navigate(prevPath);
    } catch (error) {
      logger.error("Error updating user:", error);
      showError(
        getErrorMessage(error) || error?.response?.data?.message || "Failed to update user."
      );
    } finally {
      setLoading(false);
    }
  };

  if (id && !user && detailStatus === "loading") {
    return (
      <div className="p-4">
        <BackArrow to={prevPath} />
        <ContentLoader loading className="min-h-[240px]" />
      </div>
    );
  }

  if (id && !user && detailStatus === "error") {
    return (
      <div className="p-4">
        <BackArrow to={prevPath} />
        <NoDataFound message="Could not load this user. They may have been removed or you may lack permission." />
      </div>
    );
  }

  const depsError =
    teamsError != null || userRolesError != null || jobTitleListState?.status === "failed";

  const pageReady =
    user != null &&
    !teamsLoading &&
    teamsData != null &&
    !userRolesLoading &&
    userRolesLoaded &&
    jobTitleListState?.status === "succeeded" &&
    !depsError;

  if (!pageReady) {
    const msg = depsError ? "Failed to load required form data." : null;
    return (
      <div className="p-4">
        <BackArrow to={prevPath} />
        {msg ? <NoDataFound message={msg} /> : <ContentLoader loading className="min-h-[240px]" />}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="w-full flex-1">
        <div className="p-0">
          <div className="w-full">
            <div className="flex flex-wrap -mx-4 mt-2 mb-1 items-center justify-between">
              <div className="w-full px-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <BackArrow to={prevPath} />
                  <div>
                    <Typography variant="h4" className="mb-0 text-theme-primary">
                      Edit user
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
            <Typography variant="text2" className="text-text-secondary mb-3">
              Update user role & access
            </Typography>

            <div
              className="relative flex flex-col min-w-0 rounded-md border border-border-primary shadow-sm mb-5 overflow-y-auto bg-surface-primary"
              style={{ height: "calc(100vh - 300px)" }}
            >
              <div className="flex-1 p-6">
                <form onSubmit={handleSubmit(onSubmit)} className="pb-4">
                  <div className="mb-6">
                    <Typography variant="text1" className="font-semibold mb-4 text-theme-primary">
                      Identity & Role
                    </Typography>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Controller
                          name="roleId"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              {...field}
                              label="Role"
                              options={roleOptions}
                              placeholder="Select Role"
                              error={errors.roleId?.message}
                              required={true}
                              searchable={true}
                              className="text-sm"
                            />
                          )}
                        />
                      </div>
                      <div>
                        <Label htmlFor="firstName" required>
                          First Name
                        </Label>
                        <Input
                          type="text"
                          {...register("firstName")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.firstName ? "border-red-500" : ""
                          }`}
                          id="firstName"
                          placeholder="Enter first name"
                          noWrapper={true}
                        />
                        <ErrorMsg message={errors.firstName?.message} className="mt-1" />
                      </div>
                      <div>
                        <Label htmlFor="lastName" required>
                          Last Name
                        </Label>
                        <Input
                          type="text"
                          {...register("lastName")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.lastName ? "border-red-500" : ""
                          }`}
                          id="lastName"
                          placeholder="Enter last name"
                          noWrapper={true}
                        />
                        <ErrorMsg message={errors.lastName?.message} className="mt-1" />
                      </div>
                      <div>
                        <Label htmlFor="email" required>
                          Email
                        </Label>
                        <Input
                          type="email"
                          {...register("email")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.email ? "border-red-500" : ""
                          }`}
                          id="email"
                          placeholder="Enter email"
                          noWrapper={true}
                        />
                        <ErrorMsg message={errors.email?.message} className="mt-1" />
                      </div>
                      <div>
                        <Label htmlFor="username">Username</Label>
                        <div className="relative">
                          <Input
                            type="text"
                            {...register("username")}
                            disabled={!isUsernameEditable}
                            className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary pr-10 ${
                              errors.username ? "border-red-500" : ""
                            } ${!isUsernameEditable ? "bg-gray-50" : ""}`}
                            id="username"
                            placeholder="Auto-generated from email"
                            noWrapper={true}
                          />
                          {!isUsernameEditable && (
                            <Button
                              type="button"
                              variant="icon"
                              size="vsm"
                              title="Edit username"
                              onClick={() => setIsUsernameEditable(true)}
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-text-secondary hover:text-theme-primary"
                            >
                              <RiEditLine size={18} />
                            </Button>
                          )}
                        </div>
                        <Typography variant="text2" className="text-text-secondary text-xs mt-1">
                          Username auto-generated from email{" "}
                          {isUsernameEditable && " • Editable via ✎ icon"}
                        </Typography>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6 pt-6 border-t border-border-primary">
                    <Typography variant="text1" className="font-semibold mb-4 text-theme-primary">
                      Access & Organization
                    </Typography>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Controller
                          name="teamId"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              {...field}
                              label="Team"
                              options={teamOptions}
                              placeholder="Select Team"
                              error={errors.teamId?.message}
                              required={true}
                              searchable={true}
                              className="text-sm"
                            />
                          )}
                        />
                      </div>
                      <div>
                        <Controller
                          name="jobTitle"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              {...field}
                              label="Job Title"
                              options={jobTitleOptions}
                              placeholder="Select Title"
                              error={errors.jobTitle?.message}
                              required={true}
                              searchable={true}
                              className="text-sm"
                            />
                          )}
                        />
                      </div>
                      <div>
                        <Label htmlFor="billingRatePerHour">Billing Rate Per Hour</Label>
                        <Input
                          type="number"
                          step="0.01"
                          {...register("billingRatePerHour")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.billingRatePerHour ? "border-red-500" : ""
                          }`}
                          placeholder="e.g. 85.5"
                          noWrapper={true}
                        />
                        <ErrorMsg message={errors.billingRatePerHour?.message} className="mt-1" />
                      </div>
                      <div>
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              {...field}
                              label="Status"
                              options={["Active", "Inactive"]}
                              placeholder="Select Status"
                              error={errors.status?.message}
                              required={true}
                              searchable={false}
                              className="text-sm"
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-6 pt-6 border-t border-border-primary">
                    <ExpandableSection
                      title="Contact details (optional)"
                      expanded={isContactExpanded}
                      onToggle={() => setIsContactExpanded(!isContactExpanded)}
                    >
                      {isContactExpanded && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="mobileNumber">Mobile Number</Label>
                            <Controller
                              name="mobileNumber"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="tel"
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(digitsOnly(e.target.value))}
                                  className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                                  id="mobileNumber"
                                  placeholder="Enter mobile number"
                                  noWrapper={true}
                                />
                              )}
                            />
                          </div>
                          <div>
                            <Label htmlFor="whatsappNumber">WhatsApp Number</Label>
                            <Controller
                              name="whatsappNumber"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="tel"
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(digitsOnly(e.target.value))}
                                  className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                                  id="whatsappNumber"
                                  placeholder="Enter WhatsApp number"
                                  noWrapper={true}
                                />
                              )}
                            />
                          </div>
                        </div>
                      )}
                    </ExpandableSection>
                  </div>

                  <div className="mb-6 pt-6 border-t border-border-primary">
                    <Typography variant="text1" className="font-semibold mb-4 text-theme-primary">
                      Preferences
                    </Typography>
                    <Controller
                      name="receiveWeeklyActivitySummary"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          id="receiveWeeklyActivitySummary"
                          checked={field.value}
                          label="Receive weekly activity summary"
                        />
                      )}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 bg-surface-primary border-t border-border-primary shadow-lg z-10 py-4 px-6">
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="danger"
            disabled={loading}
            onClick={() => navigate(prevPath)}
            className="btn-reset"
          >
            Cancel
          </Button>
          <FormSubmitButton
            type="button"
            onClick={handleSubmit(onSubmit)}
            loading={loading}
            label="Update user"
            idleIconClass="ri-save-line"
            className="font-bold"
          />
        </div>
      </div>
    </div>
  );
}
